import React, { useCallback, useRef, useState, useEffect } from 'react'
import { Responsive } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import { useIsMobile } from '../../hooks'
import { useDashboard } from '../../context/DashboardContext'
import type { PageId } from '../BottomNav'

// Define Layout type matching react-grid-layout
interface Layout {
  i: string
  x: number
  y: number
  w: number
  h: number
  minW?: number
  minH?: number
  maxW?: number
  maxH?: number
  static?: boolean
  isDraggable?: boolean
  isResizable?: boolean
}

interface Layouts {
  [breakpoint: string]: Layout[]
}

// Breakpoints matching existing CSS variables
const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }
const COLS = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }
const ROW_HEIGHT = 60

// Custom hook to measure container width (replaces WidthProvider)
function useContainerWidth(ref: React.RefObject<HTMLDivElement | null>) {
  const [width, setWidth] = useState(1200)

  useEffect(() => {
    if (!ref.current) return

    const updateWidth = () => {
      if (ref.current) {
        setWidth(ref.current.offsetWidth)
      }
    }

    // Initial measurement
    updateWidth()

    // Observe resize
    const resizeObserver = new ResizeObserver(updateWidth)
    resizeObserver.observe(ref.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [ref])

  return width
}

interface DashboardGridProps {
  children: React.ReactNode
  onNavigate: (page: PageId) => void
}

export function DashboardGrid({ children, onNavigate }: DashboardGridProps) {
  const { layouts, setLayouts, widgets, isEditing } = useDashboard()
  const isMobile = useIsMobile()
  const containerRef = useRef<HTMLDivElement>(null)
  const width = useContainerWidth(containerRef)

  const handleLayoutChange = useCallback(
    (currentLayout: Layout[], allLayouts: Layouts) => {
      // Only update if not on mobile (mobile layouts are static)
      if (!isMobile) {
        setLayouts(allLayouts)
      }
    },
    [isMobile, setLayouts]
  )

  // Filter layouts to only include enabled widgets
  const enabledWidgetIds = widgets.filter((w) => w.enabled).map((w) => w.id)
  const filteredLayouts: Layouts = {}
  Object.keys(layouts).forEach((breakpoint) => {
    filteredLayouts[breakpoint] = layouts[breakpoint].filter((l: Layout) =>
      enabledWidgetIds.includes(l.i)
    )
  })

  // Cast to any to avoid type issues with react-grid-layout's complex type definitions
  const ResponsiveAny = Responsive as unknown as React.ComponentType<{
    className?: string
    width: number
    layouts: Layouts
    breakpoints: Record<string, number>
    cols: Record<string, number>
    rowHeight: number
    isDraggable: boolean
    isResizable: boolean
    onLayoutChange: (currentLayout: Layout[], allLayouts: Layouts) => void
    draggableHandle: string
    resizeHandles: string[]
    margin: [number, number]
    containerPadding: [number, number]
    useCSSTransforms: boolean
    compactType: 'vertical' | 'horizontal' | null
    children: React.ReactNode
  }>

  return (
    <div ref={containerRef} className="dashboard-grid-container">
      <ResponsiveAny
        className="dashboard-grid"
        width={width}
        layouts={filteredLayouts}
        breakpoints={BREAKPOINTS}
        cols={COLS}
        rowHeight={ROW_HEIGHT}
        isDraggable={!isMobile && isEditing}
        isResizable={!isMobile && isEditing}
        onLayoutChange={handleLayoutChange}
        draggableHandle=".widget-drag-handle"
        resizeHandles={['se']}
        margin={[16, 16]}
        containerPadding={[0, 0]}
        useCSSTransforms={true}
        compactType="vertical"
      >
        {children}
      </ResponsiveAny>
    </div>
  )
}

export { BREAKPOINTS, COLS, ROW_HEIGHT }
